import axios from 'axios'
import { BASE_URL } from '../constants/config'
import { getJwtToken, clearJwtTokenCache } from '../utils/jiraContext'
import { emitLicenseRequired } from '../utils/licenseManager'

/**
 * AUDIT API
 *
 * API client for retrieving execution audit logs.
 * Provides history of status changes and bug link/unlink events.
 *
 * Endpoints:
 * - GET /testscenarios/{id}/runs/{runId}/audit                          - Get full run audit log
 * - GET /testscenarios/{id}/runs/{runId}/audit/testcases/{testCaseId}   - Get test case execution audit
 * - GET /testscenarios/{id}/runs/{runId}/audit/teststeps/{testStepId}   - Get test step execution audit
 */

// Create axios instance for audit API
const auditClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
})

/**
 * REQUEST INTERCEPTOR
 * Attaches JWT token to all requests.
 */
auditClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getJwtToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get JWT token for audit API:', error)
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * RESPONSE INTERCEPTOR
 * Handles authentication errors.
 */
auditClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Audit API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    })

    // Handle license validation failures - 402 means license is invalid/expired
    if (error.response?.status === 402) {
      emitLicenseRequired(error.response?.data?.message || 'License required')
    }

    if (error.response?.status === 401) {
      clearJwtTokenCache()
    }

    return Promise.reject(error)
  }
)

// ============================================================================
// AUDIT LOG RETRIEVAL
// ============================================================================

/**
 * GET TEST CASE EXECUTION AUDIT LOG
 *
 * Retrieves the audit history for a specific test case execution.
 * Includes status changes and bug link/unlink events.
 *
 * Action types in response:
 * - CREATED: Initial status set when execution created
 * - UPDATED: Status changed to new value
 * - BUG_LINKED: Jira bug linked to execution
 * - BUG_UNLINKED: Jira bug unlinked from execution
 *
 * @param {number|string} testScenarioId - The test scenario ID
 * @param {number|string} runId - The test run ID
 * @param {number|string} testCaseId - The test case ID
 * @returns {Promise<Array>} Array of audit log entries
 */
export const getTestCaseExecutionAuditLog = async (testScenarioId, runId, testCaseId) => {
  try {
    const response = await auditClient.get(
      `/testscenarios/${testScenarioId}/runs/${runId}/audit/testcases/${testCaseId}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching test case execution audit log:', error)
    throw error
  }
}

/**
 * GET TEST STEP EXECUTION AUDIT LOG
 *
 * Retrieves the audit history for a specific test step execution.
 * Includes status changes and bug link/unlink events.
 *
 * Action types in response:
 * - CREATED: Initial status set when execution created
 * - UPDATED: Status changed to new value
 * - BUG_LINKED: Jira bug linked to execution
 * - BUG_UNLINKED: Jira bug unlinked from execution
 *
 * @param {number|string} testScenarioId - The test scenario ID
 * @param {number|string} runId - The test run ID
 * @param {number|string} testStepId - The test step ID
 * @returns {Promise<Array>} Array of audit log entries
 */
export const getTestStepExecutionAuditLog = async (testScenarioId, runId, testStepId) => {
  try {
    const response = await auditClient.get(
      `/testscenarios/${testScenarioId}/runs/${runId}/audit/teststeps/${testStepId}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching test step execution audit log:', error)
    throw error
  }
}

/**
 * GET TEST RUN AUDIT LOG
 *
 * Retrieves the complete audit history for all executions in a test run.
 * Useful for viewing run-level activity summary.
 *
 * @param {number|string} testScenarioId - The test scenario ID
 * @param {number|string} runId - The test run ID
 * @returns {Promise<Array>} Array of audit log entries
 */
export const getTestRunAuditLog = async (testScenarioId, runId) => {
  try {
    const response = await auditClient.get(
      `/testscenarios/${testScenarioId}/runs/${runId}/audit`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching test run audit log:', error)
    throw error
  }
}

// ============================================================================
// ENTITY AUDIT LOG RETRIEVAL (for test case/step field changes)
// ============================================================================

/**
 * GET TEST CASE AUDIT LOG
 *
 * Retrieves the audit history for a test case entity (field changes).
 * Different from execution audit - this tracks edits to the test case itself.
 *
 * Action types in response:
 * - CREATED: Test case created
 * - UPDATED: Field modified (testCaseName, description, priority, assigneeId)
 * - DELETED: Test case deleted
 *
 * @param {number|string} testCaseId - The test case ID
 * @returns {Promise<Array>} Array of audit log entries
 */
export const getTestCaseAuditLog = async (testCaseId) => {
  try {
    const response = await auditClient.get(`/audit/testcases/${testCaseId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test case audit log:', error)
    throw error
  }
}

/**
 * GET TEST STEP AUDIT LOG
 *
 * Retrieves the audit history for a test step entity (field changes).
 * Different from execution audit - this tracks edits to the test step itself.
 *
 * Action types in response:
 * - CREATED: Test step created
 * - UPDATED: Field modified (stepAction, testData, expectedResult)
 * - DELETED: Test step deleted
 *
 * @param {number|string} testStepId - The test step ID
 * @returns {Promise<Array>} Array of audit log entries
 */
export const getTestStepAuditLog = async (testStepId) => {
  try {
    const response = await auditClient.get(`/audit/teststeps/${testStepId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test step audit log:', error)
    throw error
  }
}
